import * as React from 'react'
import { ChangeEvent, useEffect, useMemo, useRef } from 'react'
import { useStore } from 'effector-react'

import { useClientProfile } from './hooks'
import {
  setClientBan,
  setManualTransfer,
  submitManualTransfer,
  setClientUnban,
  setTestUserMounted,
  toggleClientPremiumStatus,
} from './model'
import { path, role } from '../../../libs'
import { $userRole } from '../../../features/user'
import { Guard } from '../../../features/session/guard'
import {
  getRolesByGuardRules,
  PERMISSIONS,
} from '../../../features/session/constants'

import {
  Tabs,
  Typography,
  Flex,
  Space,
  Tag,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Popconfirm,
  Skeleton,
  Card,
  Badge,
} from 'antd'
import {
  CopyOutlined,
  EditOutlined,
  ExperimentOutlined,
  CrownOutlined,
} from '@ant-design/icons'
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormInput,
  CFormTextarea,
  CRow,
} from '@coreui/react-pro'

import { WalletsWidget } from '../widgets/wallet'
import { AccountsWidget } from '../widgets/account'
import { BonusAccountsWidget } from '../widgets/bonus'
import { Bonus150Widget } from '../widgets/bonus150'
import { HistoryLoginsWidget } from '../widgets/historyLogin'
import { HistoriesWidget } from '../../verificationV2/atoms/history'
import { MoneyHistoryList } from '../../../views/widgets/moneyHistoryList'
import { GroupedClientPursesList } from '../../../views/widgets/groupedClientPurses'
import { CrownList } from '../widgets/crown'
import { CopytradeHistoryWidget } from '../widgets/copytradeHistory'
import { ClientAttachmentHistoryWidget } from '../widgets/clientAttachmentHistory'
import { DemoContestClient } from '../widgets/demoContestClient'
import { DepositBonus } from '../widgets/depositBonus'
import { Notifications } from '../widgets/notifications'
import { ClientProfile } from '../widgets/ClientProfile/components'
import { DocumentsWidget } from '../widgets/documentsWidget'
import { NovaClient } from '../widgets/novaClient/components'
import { TransactionList } from 'src/views/widgets/transactionList'
import { useParams, useSearchParams } from 'react-router-dom'
import {
  PaymentMethods,
  useGatewayBlocksBadge,
} from '../widgets/PaymentMethods'

const { Text, Title } = Typography

const AVATAR_COLORS = ['#3b82f6', '#a855f7', '#06b6d4', '#22c55e', '#f59e0b']
const avatarColor = (name: string) =>
  AVATAR_COLORS[name.charCodeAt(0) % AVATAR_COLORS.length]

export const ClientDetail = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { clientID } = useParams()
  const previousClientID = useRef(clientID)
  const {
    clientProfile,
    manualTransfer,
    manualTransferError,
    partner,
    partnerError,
    fullPartnerForClient,
  } = useClientProfile()

  useEffect(() => {
    if (previousClientID.current !== clientID) {
      setSearchParams({}, { replace: true })
      previousClientID.current = clientID
    }
  }, [clientID, setSearchParams])

  const userRole = useStore($userRole)

  const handleChange = (
    event: ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setManualTransfer({
      ...manualTransfer,
      [event.currentTarget.name]: event.currentTarget.value,
    })
  }

  const isNova = clientProfile.products?.includes('nova')

  const canEdit =
    userRole != undefined &&
    (userRole.indexOf(role.admin()) >= 0 ||
      userRole.indexOf(role.clientSupport.supportManager()) >= 0 ||
      userRole.indexOf(role.finances.seniorFinanceManager()) >= 0 ||
      userRole.indexOf(role.finances.leadFinanceManager()) >= 0 ||
      userRole.indexOf(role.dealer.dealer()) >= 0)

  const canBan =
    userRole &&
    (userRole.indexOf(role.admin()) >= 0 ||
      userRole.indexOf(role.verificatorManager.verificatorAgent()) >= 0 ||
      userRole.indexOf(role.finances.seniorFinanceManager()) >= 0 ||
      userRole.indexOf(role.finances.leadFinanceManager()) >= 0 ||
      userRole.indexOf(role.clientSupport.supportAgent()) >= 0 ||
      userRole.indexOf(role.clientSupport.supportManager()) >= 0)

  const hasPermission = (perm: string) => {
    if (!userRole || !userRole.includes(role.logged())) return false
    return getRolesByGuardRules(perm).some((r) => userRole.includes(r))
  }

  const blockedGatewaysCount = useGatewayBlocksBadge(
    hasPermission('view_client_profile_payment_methods')
      ? clientProfile.clientId
      : '',
  )

  const tabItems = useMemo(() => {
    const items: {
      key: string
      label: React.ReactNode
      children: React.ReactNode
      permission?: string
    }[] = [
      {
        key: 'detail',
        label: 'Detail',
        permission: 'view_client_profile_detail',
        children: (
          <ClientProfile
            client={clientProfile}
            partner={partner}
            partnerError={partnerError}
            fullPartner={fullPartnerForClient}
          />
        ),
      },
      {
        key: 'accounts',
        label: 'Accounts',
        permission: 'view_client_profile_accounts',
        children: <AccountsWidget />,
      },
      {
        key: 'wallets',
        label: 'Wallets',
        permission: 'view_client_profile_wallets',
        children: <WalletsWidget />,
      },
      {
        key: 'payment',
        label: (
          <Badge count={blockedGatewaysCount} offset={[10, -2]} size="small">
            Payment methods
          </Badge>
        ),
        permission: 'view_client_profile_payment_methods',
        children: (
          <Card>
            <PaymentMethods />
          </Card>
        ),
      },
      {
        key: 'documents',
        label: 'Documents',
        permission: 'view_client_profile_documents',
        children: (
          <>
            {clientProfile.clientId !== '' && (
              <HistoriesWidget
                clientId={clientProfile.clientId}
                requestId={''}
              />
            )}
            <Guard permission="view_uploaded_image_verification">
              <DocumentsWidget clientProfile={clientProfile} />
            </Guard>
          </>
        ),
      },
      {
        key: 'activity',
        label: 'Client activity',
        permission: 'view_client_profile_activity',
        children: <HistoryLoginsWidget />,
      },
      {
        key: 'bonus',
        label: 'Bonus 111',
        permission: 'view_client_profile_bonus111',
        children:
          clientProfile.clientId !== '' ? <BonusAccountsWidget /> : null,
      },
      {
        key: 'bonus150',
        label: 'Bonus 150',
        permission: 'view_client_profile_bonus150',
        children: clientProfile.clientId !== '' ? <Bonus150Widget /> : null,
      },
      {
        key: 'history',
        label: 'History',
        permission: 'view_client_profile_history',
        children: clientProfile.clientId ? (
          <TransactionList entityId={clientProfile.clientId} />
        ) : null,
      },
      {
        key: 'money',
        label: 'Money history',
        permission: 'view_client_profile_money_history',
        children: (
          <CCard>
            <CCardHeader>
              <strong>Money History</strong>
            </CCardHeader>
            <CCardBody>
              {clientProfile.clientId !== '' && (
                <MoneyHistoryList
                  entityId={clientProfile.clientId}
                  client={{
                    name: clientProfile.fullName,
                    email: clientProfile.email,
                    country: clientProfile.country,
                  }}
                />
              )}
            </CCardBody>
          </CCard>
        ),
      },
      {
        key: 'grouped',
        label: 'Client grouped purses',
        children:
          clientProfile.clientId !== '' ? (
            <GroupedClientPursesList entityId={clientProfile.clientId} />
          ) : null,
      },
      {
        key: 'crown',
        label: 'Crown',
        permission: 'view_client_profile_crown',
        children: (
          <>
            <CCard className="mb-4">
              <CCardHeader>
                <strong>Total</strong>
              </CCardHeader>
              <CCardBody>
                <CRow className="mb-3">
                  <CCol sm={4}>Total crown: {clientProfile.crown}</CCol>
                </CRow>
                <Guard
                  permission={PERMISSIONS.MANUAL_GIFTSHOP_CROWN_TRANSFER}
                  mode="permission"
                >
                  <CForm>
                    {manualTransferError.code !== 0 && (
                      <div className="mt-2 text-danger">
                        Error:{' '}
                        {manualTransferError.message === 'not allowed'
                          ? 'Данный клиент не учавствует в промо'
                          : manualTransferError.message}
                      </div>
                    )}
                    <CRow className="mb-3">
                      <CCol sm={4}>
                        <CFormInput
                          type="text"
                          id="amount"
                          name="amount"
                          value={manualTransfer.amount}
                          onChange={handleChange}
                          label="Amount"
                        />
                      </CCol>
                    </CRow>
                    <CRow className="mb-3">
                      <CCol sm={4}>
                        <CFormTextarea
                          name="commentForUser"
                          id="commentForUser"
                          value={manualTransfer.commentForUser}
                          onChange={handleChange}
                          label="Comment for user(not yet visible to the user)"
                        />
                      </CCol>
                    </CRow>
                  </CForm>
                  <CButton
                    className="mb-3"
                    onClick={() =>
                      submitManualTransfer({
                        clientId: clientProfile.clientId,
                        amount: manualTransfer.amount,
                        commentForUser: manualTransfer.commentForUser,
                      })
                    }
                  >
                    Change total crown
                  </CButton>
                </Guard>
              </CCardBody>
            </CCard>
            <CCard>
              <CCardHeader>
                <strong>Crown history</strong>
              </CCardHeader>
              <CCardBody>
                {clientProfile.clientId !== '' && (
                  <CrownList entityId={clientProfile.clientId} />
                )}
              </CCardBody>
            </CCard>
          </>
        ),
      },
      {
        key: 'copytrade',
        label: 'Copytrade History',
        permission: 'view_client_profile_copytrade',
        children:
          clientProfile.clientId !== '' ? <CopytradeHistoryWidget /> : null,
      },
      {
        key: 'attachment',
        label: 'Attachment History',
        permission: 'view_client_profile_attach_history',
        children: <ClientAttachmentHistoryWidget />,
      },
      {
        key: 'demo',
        label: 'Demo contest',
        permission: 'view_client_profile_demo',
        children: <DemoContestClient />,
      },
      {
        key: 'depositBonus',
        label: 'Deposit bonus',
        children: <DepositBonus />,
      },
      {
        key: 'notifications',
        label: 'Notifications',
        children: <Notifications />,
      },
    ]

    if (isNova) {
      items.push({
        key: 'nova',
        label: 'Nova',
        children: (
          <CRow>
            <CCol xs={12}>
              <CCard className="mb-4">
                <CCardHeader>
                  <strong>Nova</strong>
                </CCardHeader>
                <CCardBody>
                  <NovaClient />
                </CCardBody>
              </CCard>
            </CCol>
          </CRow>
        ),
      })
    }

    return items.filter(
      (item) => !item.permission || hasPermission(item.permission),
    )
  }, [
    clientProfile,
    partner,
    partnerError,
    fullPartnerForClient,
    manualTransfer,
    manualTransferError,
    userRole,
    isNova,
    blockedGatewaysCount,
  ])

  const defaultTab = 'detail'
  const urlTab = searchParams.get('tab')
  const activeTab =
    tabItems.find((tab) => tab.key === urlTab)?.key || defaultTab

  const handleTabChange = (key: string) => {
    setSearchParams({ tab: key })
  }

  const handleCopyId = () => {
    navigator.clipboard?.writeText(clientProfile.clientId)
  }

  const handleCopyEmail = () => {
    navigator.clipboard?.writeText(clientProfile.email)
  }

  if (!clientProfile.clientId) {
    return (
      <div>
        <div
          style={{
            background: 'var(--crm-bg)',
            borderBottom: '1px solid var(--crm-border)',
            padding: '16px 20px',
          }}
        >
          <Flex align="center" gap={14} style={{ marginBottom: 14 }}>
            <Skeleton.Avatar active size={48} />
            <div style={{ flex: 1 }}>
              <Skeleton.Input
                active
                size="small"
                style={{ width: 200, marginBottom: 6 }}
              />
              <Skeleton.Input active size="small" style={{ width: 280 }} />
            </div>
          </Flex>
          <Flex gap={8}>
            {[80, 60, 100, 70].map((w, i) => (
              <Skeleton.Button
                key={i}
                active
                size="small"
                style={{ width: w }}
              />
            ))}
          </Flex>
        </div>
        <div style={{ padding: '16px 20px' }}>
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      </div>
    )
  }

  return (
    <div>
      {/* CLIENT HEADER */}
      <div
        style={{
          background: 'var(--crm-bg)',
          borderBottom: '1px solid var(--crm-border)',
          padding: '16px 20px 0',
        }}
      >
        <Flex
          align="flex-start"
          justify="space-between"
          style={{ marginBottom: 14 }}
        >
          <Flex align="center" gap={14}>
            <Avatar
              size={48}
              src={
                clientProfile.avatarUrl?.length > 0
                  ? clientProfile.avatarUrl
                  : undefined
              }
              style={{
                background: avatarColor(clientProfile.fullName || 'A'),
                fontSize: 18,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              {(clientProfile.firstName || 'A').slice(0, 2).toUpperCase()}
            </Avatar>
            <div>
              <Flex align="center" gap={8} wrap="wrap">
                <Title level={5} style={{ margin: 0, fontSize: 18 }}>
                  {clientProfile.fullName}
                </Title>
                {!clientProfile.isTest && (
                  <Tag color="blue" style={{ fontSize: 10 }}>
                    Real user
                  </Tag>
                )}
                {clientProfile.isTest && (
                  <Tag
                    color="purple"
                    icon={<ExperimentOutlined />}
                    style={{ fontSize: 10 }}
                  >
                    Test
                  </Tag>
                )}
                {clientProfile.isPremium && (
                  <Tag
                    color="gold"
                    icon={<CrownOutlined />}
                    style={{ fontSize: 10 }}
                  >
                    Premium
                  </Tag>
                )}
                {clientProfile.banned && (
                  <Tag color="error" style={{ fontSize: 10 }}>
                    Banned
                  </Tag>
                )}
                {clientProfile.category && (
                  <Tag
                    color={
                      clientProfile.category === 'untrusted'
                        ? 'error'
                        : clientProfile.category === 'trusted'
                        ? 'success'
                        : undefined
                    }
                    style={{ fontSize: 10 }}
                  >
                    {clientProfile.category}
                  </Tag>
                )}
              </Flex>
              <Flex align="center" gap={8} style={{ marginTop: 4 }}>
                <Text type="secondary" style={{ fontSize: 16 }}>
                  ID: {clientProfile.clientId}
                </Text>
                <Tooltip title="Copy ID">
                  <CopyOutlined
                    style={{
                      fontSize: 18,
                      color: 'var(--crm-text-secondary)',
                      cursor: 'pointer',
                    }}
                    onClick={handleCopyId}
                  />
                </Tooltip>
                <Divider type="vertical" style={{ margin: '0 2px' }} />
                <Text type="secondary" style={{ fontSize: 16 }}>
                  {clientProfile.email}
                </Text>
                <Tooltip title="Copy email">
                  <CopyOutlined
                    style={{
                      fontSize: 18,
                      color: 'var(--crm-text-secondary)',
                      cursor: 'pointer',
                    }}
                    onClick={handleCopyEmail}
                  />
                </Tooltip>
              </Flex>
            </div>
          </Flex>

          <Flex align="center" gap={24}>
            <Space size={6}>
              {!clientProfile.isTest && (
                <Popconfirm
                  title="Set this client as test?"
                  okText="Yes"
                  cancelText="Cancel"
                  onConfirm={() => setTestUserMounted()}
                >
                  <Button size="small" icon={<ExperimentOutlined />}>
                    Set Test
                  </Button>
                </Popconfirm>
              )}
              <Popconfirm
                title={
                  clientProfile.isPremium
                    ? 'Remove Premium status?'
                    : 'Grant Premium status?'
                }
                okText="Yes"
                cancelText="Cancel"
                onConfirm={() =>
                  toggleClientPremiumStatus({
                    status: !clientProfile.isPremium,
                  })
                }
              >
                <Button size="small" icon={<CrownOutlined />}>
                  {clientProfile.isPremium ? 'Unset Premium' : 'Set Premium'}
                </Button>
              </Popconfirm>
              {canBan && !clientProfile.banned && (
                <Popconfirm
                  title="Ban this client?"
                  description="The client will lose access to all services."
                  okText="Ban"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  onConfirm={() =>
                    setClientBan({ ...clientProfile, banned: true })
                  }
                >
                  <Button size="small" danger>
                    Ban
                  </Button>
                </Popconfirm>
              )}
              {canBan && clientProfile.banned && (
                <Popconfirm
                  title="Unban this client?"
                  okText="Unban"
                  cancelText="Cancel"
                  onConfirm={() =>
                    setClientUnban({ ...clientProfile, banned: false })
                  }
                >
                  <Button size="small">Unban</Button>
                </Popconfirm>
              )}
            </Space>

            <Divider type="vertical" style={{ height: 24, margin: 0 }} />

            {canEdit && (
              <Button
                size="small"
                icon={<EditOutlined />}
                type="primary"
                href={path.clients.edit(clientProfile.clientId)}
              >
                Edit
              </Button>
            )}
          </Flex>
        </Flex>

        <Tabs
          size="small"
          activeKey={activeTab}
          onChange={handleTabChange}
          items={tabItems.map(({ key, label }) => ({ key, label }))}
          style={{ marginBottom: -1 }}
        />
      </div>

      {/* TAB CONTENT */}
      <div style={{ padding: '16px 0' }}>
        <React.Fragment key={clientProfile.clientId}>
          {tabItems.find((t) => t.key === activeTab)?.children}
        </React.Fragment>
      </div>
    </div>
  )
}
